'use client'

import { useCallback, useEffect, useMemo } from 'react'
import { createContainer } from 'unstated-next'

import { useHeap } from '../_components/chrome/scripts/HeapScript'
import { cookieKeys } from '../_config/Cookies.config'
import {
  SessionAttributes,
  convertCookiesToSessionAttributes,
  sessionStorageKeys,
} from '../_config/Session.config'

import { useCookies } from './CookiesProvider.client'
import { useLocalization } from './LocalizationProvider.client'

const NEXT_PUBLIC_FIGS_ENV = process.env.NEXT_PUBLIC_FIGS_ENV

type UseSessionOutput = {
  attributes: SessionAttributes
  setStylePreference: (newStylePreference: SessionAttributes['stylePreference']) => void
}

const useSessionImpl = (): UseSessionOutput => {
  const { addHeapEventProperties } = useHeap()
  const { locale } = useLocalization()
  const [cookies, setCookie] = useCookies([
    cookieKeys.initialSessionIndicator.key,
    cookieKeys.stylePreference.key,
    cookieKeys.sessionId.key,
    cookieKeys.visitorId.key,
  ])

  const initialSessionCookie = cookies[cookieKeys.initialSessionIndicator.key]
  const stylePreferenceCookie = cookies[cookieKeys.stylePreference.key]
  const sessionIdCookie = cookies[cookieKeys.sessionId.key]
  const visitorIdCookie = cookies[cookieKeys.visitorId.key]

  const attributes = useMemo<UseSessionOutput['attributes']>(() => {
    const valuesFromCookies = convertCookiesToSessionAttributes({
      initialSessionCookie,
      stylePreferenceCookie,
      sessionIdCookie,
      visitorIdCookie,
    })
    return {
      ...valuesFromCookies,
      sessionId: valuesFromCookies.sessionId ?? `js-${crypto.randomUUID()}`,
      visitorId: valuesFromCookies.visitorId ?? `js-${crypto.randomUUID()}`,
    }
  }, [initialSessionCookie, sessionIdCookie, stylePreferenceCookie, visitorIdCookie])

  const setStylePreference = useCallback<UseSessionOutput['setStylePreference']>(
    newStylePreference => {
      if (newStylePreference) {
        setCookie(
          cookieKeys.stylePreference.key,
          newStylePreference,
          cookieKeys.stylePreference.options
        )
      }
    },
    [setCookie]
  )

  useEffect(() => {
    // CloudFront sets these cookies. Setting in JS will cause Safari ITP to shrink their duration.
    // Only doing this here to support local dev and PR specific sites that are not through a CDN.
    if (NEXT_PUBLIC_FIGS_ENV === 'local' || NEXT_PUBLIC_FIGS_ENV === 'live') {
      if (!sessionIdCookie) {
        setCookie(cookieKeys.sessionId.key, attributes.sessionId, cookieKeys.sessionId.options)
      }
      if (!visitorIdCookie) {
        setCookie(cookieKeys.visitorId.key, attributes.visitorId, cookieKeys.visitorId.options)
      }
    }
  }, [attributes.sessionId, attributes.visitorId, sessionIdCookie, setCookie, visitorIdCookie])

  // We store an array of locales that were used in session storage and then update the Heap session to include them.
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const usedLocales: string[] = []
      const previouslyUsedLocalesString = globalThis.sessionStorage?.getItem(
        sessionStorageKeys.usedLocales
      )

      if (previouslyUsedLocalesString) {
        const previouslyUsedLocales = JSON.parse(previouslyUsedLocalesString)
        for (let locale of previouslyUsedLocales) {
          usedLocales.push(locale)
        }
      }
      if (typeof locale === 'string') {
        if (!usedLocales.includes(locale)) {
          usedLocales.push(locale)
        }
      }
      const usedLocalesString = JSON.stringify(usedLocales)

      globalThis.sessionStorage?.setItem(sessionStorageKeys.usedLocales, usedLocalesString)

      addHeapEventProperties({
        props: {
          '@figs:used-locales': usedLocalesString,
        },
      })
    }
  }, [locale, addHeapEventProperties])

  // On page load we store any discount query param in session storage.
  // Then we pass that on to the Shopify checkout later.
  useEffect(() => {
    if (typeof window === 'undefined') return

    const searchParams = new URLSearchParams(window.location.search)
    const discountQueryParam = searchParams.get('discount')
    if (discountQueryParam) {
      if (discountQueryParam.trim() === '') {
        globalThis.sessionStorage?.removeItem(sessionStorageKeys.discount)
      } else {
        globalThis.sessionStorage?.setItem(sessionStorageKeys.discount, discountQueryParam)
      }
    }
  }, [])

  return {
    attributes,
    setStylePreference,
  }
}

const SessionContainer = createContainer(useSessionImpl)
SessionContainer.Provider.displayName = 'SessionProviderImpl'
export const SessionProvider = SessionContainer.Provider
export const useSession = SessionContainer.useContainer
